import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Spin, Modal, Select, Divider, Tooltip, Button } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { TreatmentTemplate } from 'types/treatmentTemplate';
import useGetTreatmentTemplates from 'hooks/videoSetting/useGetTreatmentTemplates';
import TreatmentPlanModal from 'pages/dashboard/CalendarSettingPage/components/VideoVisitPage/components/TreatmentPlanModal';
import { getHasShowVisitTemplateGuide, setHasShowVisitTemplateGuide } from 'utils/localstore';
import s from './s.module.less';

const GUIDE_AUTO_DISMISS_MS = 5000;

interface TreatmentTemplateSelectionProps {
    specialty?: string;
    visitType?: string;
    isStandalone?: boolean;
    // 受控选中值：选中状态由父组件持有，内联面板与全屏弹窗两个实例共享
    value?: number;
    onChange?: (templateId: number) => void;
    needInitContent?: boolean;
    disabled?: boolean;
    // 会中（MeetingPage）一次性引导气泡；同一 provider 只出现一次，其他使用方不传即不显示
    enableGuide?: boolean;
}

const TreatmentTemplateSelection: React.FC<TreatmentTemplateSelectionProps> = ({
    specialty,
    visitType,
    isStandalone,
    value,
    onChange,
    needInitContent,
    disabled,
    enableGuide,
}) => {
    const [open, setOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [templates, , loading, fetchTemplates] = useGetTreatmentTemplates();
    // 首次请求是否已完成：完成前维持旧行为（不渲染），避免空态按钮在数据返回前闪现
    const [fetchDone, setFetchDone] = useState(false);
    const wasLoadingRef = useRef(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const guideShownRef = useRef(false);

    const selectedTemplate = templates.find((t) => t.id === value);

    const fetchData = useCallback(() => {
        if (specialty && visitType) {
            fetchTemplates({
                specialty,
                serviceType: visitType,
                current: 1,
                size: 50,
                orderKey: 'updatedAt',
                orderType: 'desc',
            });
        } else if (isStandalone) {
            const data: any = {
                current: 1,
                size: 50,
                orderKey: 'updatedAt',
                orderType: 'desc',
            };
            if (specialty) {
                data.specialty = specialty;
            }
            fetchTemplates(data);
        }
    }, [specialty, visitType, isStandalone, fetchTemplates]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (loading) {
            wasLoadingRef.current = true;
        } else if (wasLoadingRef.current) {
            setFetchDone(true);
        }
    }, [loading]);

    useEffect(() => {
        if (templates.length > 0 && !selectedTemplate && needInitContent) {
            const defaultTemplate = templates.find((t) => t.isDefault) ?? templates[0];
            onChange?.(defaultTemplate.id);
        }
    }, [templates, needInitContent]);

    // 模板数据就绪后弹引导；立即写入已读标记，保证刷新后不再出现
    useEffect(() => {
        if (!enableGuide || guideShownRef.current || !fetchDone || loading) {
            return;
        }
        if (getHasShowVisitTemplateGuide()) {
            return;
        }
        guideShownRef.current = true;
        setHasShowVisitTemplateGuide(true);
        setGuideOpen(true);
    }, [enableGuide, fetchDone, loading]);

    useEffect(() => {
        if (!guideOpen) {
            return undefined;
        }
        const dismiss = () => setGuideOpen(false);
        const timer = setTimeout(dismiss, GUIDE_AUTO_DISMISS_MS);
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target?.closest(`.${s.guideOverlay}`)) {
                dismiss();
            }
        };
        // 键盘输入（含在编辑器里打字）即关闭；气泡挂在 body 上不随面板滚动，滚动时也直接关闭。
        // 延迟注册：打开瞬间编辑器可能正在自动填充默认模板，Quill 写入会触发内部 scroll，立刻注册会把刚弹出的气泡秒关
        const listenerTimer = setTimeout(() => {
            document.addEventListener('mousedown', onMouseDown);
            document.addEventListener('keydown', dismiss);
        }, 800);
        return () => {
            clearTimeout(timer);
            clearTimeout(listenerTimer);
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', dismiss);
        };
    }, [guideOpen]);

    const isEmpty = !loading && templates.length === 0;

    if (isEmpty && !fetchDone) {
        return null;
    }

    const guideText =
        templates.length === 0
            ? "Set up a visit note template for your specialty here. It autofills your visit note so you don't type everything from scratch."
            : 'Your visit note autofills from this template. Switch to a different one anytime here.';

    const renderWithGuide = (trigger: React.ReactElement) => (
        <Tooltip open={guideOpen} title={guideText} placement="topLeft" color="#fff" overlayClassName={s.guideOverlay}>
            {trigger}
        </Tooltip>
    );

    if (isEmpty) {
        return (
            <>
                {renderWithGuide(
                    <Button
                        className={s.createEmptyBtn}
                        icon={<PlusOutlined />}
                        disabled={disabled}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Create new template
                    </Button>,
                )}
                <TreatmentPlanModal
                    open={isModalOpen}
                    onCancel={() => setIsModalOpen(false)}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchData();
                    }}
                    specialty={specialty}
                    visitType={visitType}
                    isStandalone={isStandalone}
                />
            </>
        );
    }

    const handleSelect = (templateId: number) => {
        Modal.confirm({
            title: 'Apply template',
            content:
                'Applying this template will replace the content you’ve already entered in the current visit notes.',
            okText: 'Apply',
            onCancel: () => {},
            icon: null,
            width: 500,
            okButtonProps: {
                className: s.confirmButton,
            },
            onOk: () => {
                onChange?.(templateId);
                setOpen(false);
            },
            closable: true,
            wrapClassName: s.customConfirmRoot,
        });
    };

    const handleCreateSuccess = () => {
        setIsModalOpen(false);
        fetchData();
    };

    const options = templates.map((t) => ({ label: t.name, value: t.id }));

    return (
        <>
            {renderWithGuide(
                <Select
                    className={s.select}
                    loading={loading}
                    disabled={disabled}
                    options={options}
                    value={value}
                    placeholder="Select a template"
                    onChange={handleSelect}
                    dropdownRender={(menu) => (
                        <>
                            {menu}
                            <Divider className={s.divider} />
                            <div
                                className={s.createItem}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setIsModalOpen(true)}
                            >
                                <PlusOutlined className={s.plusIcon} />
                                <span>Create new template</span>
                            </div>
                        </>
                    )}
                />,
            )}
            <TreatmentPlanModal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onSuccess={handleCreateSuccess}
                specialty={specialty}
                visitType={visitType}
                isStandalone={isStandalone}
            />
        </>
    );
};

export default TreatmentTemplateSelection;
